import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Badge, Form, InputGroup, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { fetchGroupsBySearch, fetchGroupsByPagination, fetchGroupsByState } from "../services/GroupService";
import { fetchStates, getStateNameByCode } from "../services/LocationService";
import GroupData from "../models/GroupData";
import { IMAGE_BASE } from "../Config";
import { getGroupImageUrl, handleImageError } from "../utils/imageUtils";
import StateData from "../models/StateData";

const GroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [states, setStates] = useState<StateData[]>([]);
  const [stateNames, setStateNames] = useState<{ [key: string]: string }>({});
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadGroups();
      loadStates();
    }
  }, []);

  const hasLoadedRef = useRef(false);

  const loadStates = async () => {
    try {
      const statesData = await fetchStates();
      setStates(statesData);
    } catch (error) {
      console.error("Failed to load states");
    }
  };

  const getStateName = async (stateCode: string) => {
    if (stateNames[stateCode]) {
      return stateNames[stateCode];
    }
    
    try {
      const name = await getStateNameByCode(stateCode);
      setStateNames(prev => ({ ...prev, [stateCode]: name }));
      return name;
    } catch (error) {
      return stateCode;
    }
  };

  const loadGroups = async (reset = false) => {
    if (loading && !reset) return; // Prevent double loading
    
    try {
      setLoading(true);
      setError(null);
      const currentPage = reset ? 1 : page;
      const response = await fetchGroupsByPagination(currentPage, 12, "createdAt");
      
      if (reset) {
        setGroups(response.groups || []);
        setPage(2);
      } else {
        setGroups(prev => [...prev, ...(response.groups || [])]);
        setPage(prev => prev + 1);
      }
      
      setHasMore(response.groups?.length === 12);
    } catch (err) {
      setError("Failed to load groups");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadGroups(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const results = await fetchGroupsBySearch(searchQuery);
      setGroups(Array.isArray(results) ? results : []);
      setHasMore(false);
    } catch (err) {
      setError("No groups found for your search");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStateFilter = async (state: string) => {
    setSelectedState(state);
    
    if (state === "" || state === "All States") {
      loadGroups(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const results = await fetchGroupsByState(state);
      setGroups(Array.isArray(results) ? results : []);
      setHasMore(false);
    } catch (err) {
      setError(`No groups found in ${state}`);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupClick = (id: string) => {
    navigate(`/group/${id}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', minHeight: '100vh' }}>
      <Container className="py-5">
        {/* Modern Header with Gradient */}
        <div className="text-center mb-5">
          <div className="d-inline-flex align-items-center mb-3">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #FFD600 0%, #810081 100%)' }}
            >
              <i className="bi bi-people-fill text-white" style={{ fontSize: '1.5rem' }}></i>
            </div>
            <div>
              <h1 className="display-5 fw-bold mb-0" style={{ color: '#810081' }}>Theater Groups</h1>
              <p className="text-muted mb-0">Discover amazing performing arts communities</p>
            </div>
          </div>
        </div>

        {/* Modern Search Section with Pills */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
          <Card.Body className="p-4">
            <Row className="g-3">
              <Col lg={8}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search theater groups..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="ps-5 py-3 border-0 bg-light"
                    style={{ borderRadius: '50px', fontSize: '16px' }}
                  />
                  <i 
                    className="bi bi-search position-absolute" 
                    style={{ left: '20px', top: '50%', transform: 'translateY(-50%)', color: '#810081' }}
                  ></i>
                  <Button 
                    onClick={handleSearch} 
                    disabled={loading}
                    className="position-absolute border-0"
                    style={{ 
                      right: '8px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      backgroundColor: '#FFD600',
                      color: '#810081',
                      borderRadius: '50px',
                      width: '40px',
                      height: '40px'
                    }}
                  >
                    {loading ? <Spinner size="sm" /> : <i className="bi bi-search"></i>}
                  </Button>
                </div>
              </Col>
              <Col lg={4}>
                <Form.Select 
                  value={selectedState} 
                  onChange={(e) => handleStateFilter(e.target.value)}
                  className="py-3 border-0 bg-light"
                  style={{ borderRadius: '50px', fontSize: '16px' }}
                >
                  <option value="">All States</option>
                  {states.map((state) => (
                    <option key={state.stateCode} value={state.stateCode}>
                      {state.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            
            {/* Active Filters Pills */}
            {(searchQuery || selectedState) && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                <small className="text-muted me-2">Active filters:</small>
                {searchQuery && (
                  <span 
                    className="badge d-flex align-items-center"
                    style={{ backgroundColor: '#810081', borderRadius: '50px', padding: '8px 12px' }}
                  >
                    Search: "{searchQuery}"
                    <button 
                      className="btn-close btn-close-white ms-2" 
                      style={{ fontSize: '10px' }}
                      onClick={() => {
                        setSearchQuery("");
                        loadGroups(true);
                      }}
                    ></button>
                  </span>
                )}
                {selectedState && (
                  <span 
                    className="badge d-flex align-items-center"
                    style={{ backgroundColor: '#FFD600', color: '#810081', borderRadius: '50px', padding: '8px 12px' }}
                  >
                    State: {stateNames[selectedState] || selectedState}
                    <button 
                      className="btn-close ms-2" 
                      style={{ fontSize: '10px' }}
                      onClick={() => {
                        setSelectedState("");
                        loadGroups(true);
                      }}
                    ></button>
                  </span>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Modern Results Header */}
        {!loading && (
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="mb-1" style={{ color: '#810081' }}>
                {groups.length > 0 ? `${groups.length} Groups Found` : "No Groups Found"}
              </h5>
              <small className="text-muted">
                {searchQuery && `Searching for "${searchQuery}"`}
                {selectedState && ` in ${stateNames[selectedState] || selectedState}`}
              </small>
            </div>
            {groups.length > 0 && (
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="rounded-pill"
                onClick={() => {
                  const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                  setSortOrder(newOrder);
                  const sortedGroups = [...groups].sort((a, b) => {
                    return newOrder === 'asc' 
                      ? a.name.localeCompare(b.name)
                      : b.name.localeCompare(a.name);
                  });
                  setGroups(sortedGroups);
                }}
              >
                <i className={`bi bi-sort-alpha-${sortOrder === 'asc' ? 'down' : 'up'} me-1`}></i>
                Sort {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
              </Button>
            )}
          </div>
        )}

        {/* Modern Error Message */}
        {error && (
          <Alert 
            variant="" 
            className="border-0 text-center mb-4"
            style={{ 
              backgroundColor: 'rgba(255, 193, 7, 0.1)', 
              color: '#810081',
              borderRadius: '15px',
              padding: '20px'
            }}
          >
            <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: '#FFD600' }}></i>
            {error}
          </Alert>
        )}

        {/* Modern Groups Grid */}
        <Row className="g-4">
          {groups.map((group) => (
            <Col key={group._id} lg={4} md={6}>
              <Card 
                className="h-100 border-0 group-card position-relative" 
                onClick={() => handleGroupClick(group._id)}
                style={{ 
                  cursor: "pointer", 
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(129, 0, 129, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(129, 0, 129, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow = "0 4px 20px rgba(129, 0, 129, 0.1)";
                }}
              >
                <div className="position-relative overflow-hidden">
                  <Card.Img 
                    variant="top" 
                    src={getGroupImageUrl(group.groupImage)} 
                    style={{ height: "220px", objectFit: "cover" }}
                    onError={(e) => handleImageError(e, 'GROUP')}
                  />
                  <div className="position-absolute top-0 start-0 w-100 h-100" 
                       style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)' }}></div>
                  
                  {/* Featured Badge */}
                  {group.isFeatured && (
                    <div className="position-absolute top-0 end-0 m-3">
                      <span 
                        className="badge d-flex align-items-center"
                        style={{ 
                          backgroundColor: '#FFD600', 
                          color: '#810081', 
                          borderRadius: '50px',
                          padding: '8px 12px',
                          fontWeight: '600'
                        }}
                      >
                        <i className="bi bi-star-fill me-1"></i>Featured
                      </span>
                    </div>
                  )}
                  
                  {/* Hover Overlay */}
                  <div 
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ 
                      background: 'rgba(129, 0, 129, 0.8)', 
                      opacity: 0, 
                      transition: 'opacity 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                  >
                    <Button 
                      className="rounded-pill border-0"
                      style={{ backgroundColor: '#FFD600', color: '#810081' }}
                    >
                      <i className="bi bi-eye me-2"></i>View Details
                    </Button>
                  </div>
                </div>
                
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <Card.Title className="fw-bold mb-0" style={{ color: '#810081', fontSize: '1.1rem' }}>
                      {group.name}
                    </Card.Title>
                  </div>
                  
                  <Card.Text className="text-muted mb-3" style={{ fontSize: "0.9rem", lineHeight: '1.5' }}>
                    {group.description && group.description.length > 100 
                      ? `${group.description.substring(0, 100)}...` 
                      : group.description || "Passionate theater group bringing stories to life"}
                  </Card.Text>
                  
                  {/* Location Pill */}
                  <div className="mb-3">
                    <span 
                      className="badge d-inline-flex align-items-center"
                      style={{ 
                        backgroundColor: 'rgba(129, 0, 129, 0.1)', 
                        color: '#810081', 
                        borderRadius: '50px',
                        padding: '6px 12px',
                        fontSize: '0.8rem'
                      }}
                    >
                      <i className="bi bi-geo-alt me-1"></i>
                      <StateDisplay stateCode={group.state} country={group.country} getStateName={getStateName} />
                    </span>
                  </div>
                  
                  {/* Specialties Pills */}
                  {group.specialties && group.specialties.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mb-3">
                      {group.specialties.slice(0, 3).map((specialty, index) => (
                        <span 
                          key={index} 
                          className="badge"
                          style={{ 
                            backgroundColor: 'rgba(255, 214, 0, 0.2)', 
                            color: '#810081', 
                            borderRadius: '50px',
                            padding: '4px 8px',
                            fontSize: '0.7rem',
                            fontWeight: '500'
                          }}
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Action Button */}
                  <div className="d-flex justify-content-end">
                    <small style={{ color: '#FFD600', fontWeight: '600' }}>
                      Explore Group <i className="bi bi-arrow-right ms-1"></i>
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Modern Load More Button */}
        {hasMore && groups.length > 0 && !searchQuery && !selectedState && (
          <div className="text-center mt-5">
            <Button 
              onClick={() => loadGroups()} 
              disabled={loading}
              className="rounded-pill border-0 px-5 py-3"
              style={{ 
                background: 'linear-gradient(135deg, #FFD600 0%, #810081 100%)',
                color: 'white',
                fontWeight: '600',
                fontSize: '16px',
                boxShadow: '0 4px 15px rgba(129, 0, 129, 0.3)'
              }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Loading More...
                </>
              ) : (
                <>
                  <i className="bi bi-plus-circle me-2"></i>
                  Load More Groups
                </>
              )}
            </Button>
          </div>
        )}

        {/* Modern Empty State */}
        {!loading && groups.length === 0 && !error && (
          <Card className="border-0 text-center py-5" style={{ borderRadius: '20px', background: 'rgba(255, 255, 255, 0.8)' }}>
            <Card.Body className="py-5">
              <div 
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'linear-gradient(135deg, rgba(255, 214, 0, 0.2) 0%, rgba(129, 0, 129, 0.2) 100%)' 
                }}
              >
                <i className="bi bi-people" style={{ fontSize: '2rem', color: '#810081' }}></i>
              </div>
              <h4 className="mb-3" style={{ color: '#810081' }}>No Theater Groups Found</h4>
              <p className="text-muted mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>
                {searchQuery || selectedState 
                  ? "We couldn't find any groups matching your criteria. Try adjusting your search or filters." 
                  : "Be the first to discover amazing theater groups in your area!"}
              </p>
              {(searchQuery || selectedState) && (
                <Button 
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedState("");
                    loadGroups(true);
                  }}
                  className="rounded-pill border-0 px-4 py-2"
                  style={{ backgroundColor: '#FFD600', color: '#810081', fontWeight: '600' }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Clear All Filters
                </Button>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

// Component to display state name from code
const StateDisplay: React.FC<{ stateCode: string; country: string; getStateName: (code: string) => Promise<string> }> = ({ stateCode, country, getStateName }) => {
  const [stateName, setStateName] = useState(stateCode);

  useEffect(() => {
    const loadStateName = async () => {
      const name = await getStateName(stateCode);
      setStateName(name);
    };
    loadStateName();
  }, [stateCode, getStateName]);

  return <>{stateName}, {country}</>;
};

export default GroupsPage;
